import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'react-feather';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', path: '/home' },
    { 
      name: 'Services', 
      path: '/services',
      // subLinks: [
      //   { name: 'Tax Planning', path: '/services/tax-planning' },
      //   { name: 'Tax Filing', path: '/services/tax-filing' },
      //   { name: 'Audit Support', path: '/services/audit-support' }
      // ]
    },
    { name: 'Tax Tools', path: '/calculator' },
    { 
      name: 'Case Laws', 
      path: '/case-laws',
      subLinks: [
        { name: 'All Case Laws', path: '/case-laws' },
        { name: 'Income Tax Judgments', path: '/case-laws/income-tax' },
        { name: 'GST Rulings & Orders', path: '/case-laws/gst' }
      ]
    },
    // { name: 'Resources', path: '/resources' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50 border-b-2 border-cyan-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24"> {/* Fixed height */}
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center h-full">
            <Link to="/" className="flex items-center h-full">
              <img
                src="./assets/logo.webp"
                alt="Tax Consultant Logo"
                className="h-32 w-auto object-contain" // Large logo with proper fitting
              />
              
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1 h-full"> {/* Full height nav */}
            {navLinks.map((link, index) => (
              <div key={link.name} className="relative h-full flex items-center">
                {link.subLinks ? (
                  <div className="relative group h-full">
                    <button
                      onClick={() => toggleDropdown(index)}
                      className={`flex items-center h-full px-4 text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200 ${
                        location.pathname.startsWith(link.path) ? 'text-cyan-600 border-b-2 border-cyan-600' : ''
                      }`}
                    >
                      {link.name}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${
                        activeDropdown === index ? 'transform rotate-180' : ''
                      }`} />
                    </button>
                    
                    {/* Dropdown */}
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute left-0 mt-0 w-56 bg-white shadow-lg rounded-b-md z-50"
                        >
                          {link.subLinks.map((subLink) => (
                            <Link
                              key={subLink.name}
                              to={subLink.path}
                              className={`block px-4 py-3 text-sm ${
                                location.pathname === subLink.path
                                  ? 'bg-cyan-50 text-cyan-600'
                                  : 'text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
                              }`}
                            >
                              {subLink.name}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    to={link.path}
                    className={`flex items-center h-full px-4 text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200 ${
                      location.pathname === link.path ? 'text-cyan-600 border-b-2 border-cyan-600' : ''
                    }`}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Call to Action Button */}
          <div className="hidden md:block">
            <button
              onClick={() => navigate('/return-selection')}
              className="ml-6 whitespace-nowrap inline-flex items-center justify-center px-5 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 transition-all duration-200 hover:shadow-md"
            >
              Guest User Login
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 focus:outline-none transition-colors duration-200"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white shadow-lg overflow-hidden"
          >
            <div className="px-2 pt-2 pb-4 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <div key={link.name}>
                  {link.subLinks ? (
                    <div className="mb-1">
                      <button
                        onClick={() => toggleDropdown(navLinks.indexOf(link))}
                        className={`w-full flex justify-between items-center px-3 py-3 rounded-md text-base font-medium ${
                          location.pathname.startsWith(link.path)
                            ? 'bg-cyan-50 text-cyan-600'
                            : 'text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className={`h-5 w-5 transition-transform ${
                          activeDropdown === navLinks.indexOf(link) ? 'transform rotate-180' : ''
                        }`} />
                      </button>
                      
                      <AnimatePresence>
                        {activeDropdown === navLinks.indexOf(link) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="pl-4"
                          >
                            {link.subLinks.map((subLink) => (
                              <Link
                                key={subLink.name}
                                to={subLink.path}
                                className={`block px-3 py-2 rounded-md text-sm ${
                                  location.pathname === subLink.path
                                    ? 'bg-cyan-100 text-cyan-600'
                                    : 'text-gray-600 hover:bg-cyan-50 hover:text-cyan-600'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {subLink.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`block px-3 py-3 rounded-md text-base font-medium ${
                        location.pathname === link.path
                          ? 'bg-cyan-50 text-cyan-600'
                          : 'text-gray-700 hover:bg-cyan-50 hover:text-cyan-600'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
              <button
                onClick={() => {
                  navigate('/return-selection');
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-2 px-4 py-3 rounded-md text-base font-medium text-white bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 transition-all duration-200"
              >
                Guest User Login
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;